import React from 'react';
import { Badge } from '@org/ui';
import { Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { JoinState } from './join-utils';

type Variant = 'light' | 'dark';

interface JoinButtonProps {
  /** Optional meeting URL. When provided, the icon is a clickable link. */
  href?: string | null;
  state: JoinState;
  /** 'dark' for red/today cards (white icon), 'light' for white cards (rose icon) */
  variant?: Variant;
}

/**
 * Camera icon that indicates a live/upcoming event.
 * When `href` is provided it renders as a link to the meeting.
 * Shows a ping animation when live, pulse when starting soon.
 */
export function JoinButton({ href, state, variant = 'light' }: JoinButtonProps) {
  const { t } = useTranslation('admin');
  const dark = variant === 'dark';

  const label = t('p2e.classes.carousel.joinLabel');

  const baseClass = `relative inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-xs font-semibold tracking-wide shadow-sm transition focus:outline-none focus-visible:ring-2 ${
    dark
      ? 'border border-white/30 bg-white/20 text-white hover:bg-white/30 focus-visible:ring-white/60'
      : 'bg-[#cf2e2e] text-white hover:bg-[#b82828] focus-visible:ring-rose-300 shadow-md'
  }`;

  const pulseClass = `pointer-events-none absolute inset-0 inline-flex rounded-full ${
    dark
      ? state === 'live'
        ? 'animate-[ping_1.2s_ease-out_infinite] bg-white/40 blur-[2px]'
        : 'animate-[pulse_1.3s_ease-in-out_infinite] bg-white/30 blur-[1px]'
      : state === 'live'
        ? 'animate-[ping_1.2s_ease-out_infinite] bg-[#cf2e2e]/40 blur-[2px]'
        : 'animate-[pulse_1.3s_ease-in-out_infinite] bg-[#cf2e2e]/25 blur-[1px]'
  }`;

  const inner = (
    <>
      <span aria-hidden className={pulseClass} />
      <Video className="relative h-4 w-4" />
      <span className="relative">{label}</span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={baseClass}
        onClick={(e) => e.stopPropagation()}
        aria-label="Join Google Meet"
        title="Join Google Meet"
      >
        {inner}
      </a>
    );
  }

  return (
    <span className={baseClass} aria-label={state === 'live' ? 'Live now' : 'Starting soon'}>
      {inner}
    </span>
  );
}

interface LiveBadgeProps {
  state: JoinState;
  /** 'dark' for red/today cards, 'light' for white cards */
  variant?: Variant;
}

/**
 * "Live" / "Starting soon" badge with consistent styling and translations.
 */
export function LiveBadge({ state, variant = 'light' }: LiveBadgeProps) {
  const { t } = useTranslation('admin');
  const dark = variant === 'dark';

  const label =
    state === 'live'
      ? t('p2e.dashboard.stats.liveLabel', { defaultValue: 'Live' })
      : t('p2e.dashboard.stats.soonLabel', { defaultValue: 'Starting soon' });

  const className = dark
    ? state === 'live'
      ? 'bg-white text-[#cf2e2e] animate-pulse'
      : 'bg-white/30 text-white backdrop-blur'
    : state === 'live'
      ? 'bg-[#cf2e2e] text-white animate-pulse'
      : 'bg-[#fff1f5] text-[#cf2e2e]';

  return <Badge className={className}>{label}</Badge>;
}
